import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import { forwardRef } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'gold' | 'teal';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
}

const variants: Record<Variant, string> = {
  primary:   'bg-navy-700 text-white hover:bg-navy-600 active:bg-navy-800 shadow-sm',
  secondary: 'bg-white text-navy-700 border border-parchment-200 hover:bg-parchment-50 hover:border-navy-300',
  ghost:     'text-navy-600 hover:bg-parchment-100 hover:text-navy-900',
  danger:    'bg-red-600 text-white hover:bg-red-500 shadow-sm',
  gold:      'bg-amber-600 text-white hover:bg-amber-500 shadow-gold',
  teal:      'bg-teal-600 text-white hover:bg-teal-500 shadow-teal',
};

const sizes: Record<Size, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-6 text-base gap-2.5',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'secondary', size = 'md', loading, icon, iconRight, className, children, disabled, ...props }, ref) => (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        'btn-base font-body',
        variants[variant],
        sizes[size],
        className
      )}
      {...props}
    >
      {loading ? <Loader2 className="animate-spin shrink-0" size={size === 'sm' ? 13 : 15} /> : icon}
      {children}
      {iconRight && !loading && <span className="ml-auto">{iconRight}</span>}
    </button>
  )
);

Button.displayName = 'Button';
